
import { useState, useEffect } from 'react';
import { VitaGeneratedContent } from '@/types/wellness';
import { useVitaStorage } from '@/hooks/use-vita-storage';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FilePlus, Trash2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface VitaContentSectionProps {
  category: "nutrition" | "exercise" | "supplements" | "mindfulness" | "sleep" | "notes";
  title: string;
}

export function VitaContentSection({ category, title }: VitaContentSectionProps) {
  const [contents, setContents] = useState<VitaGeneratedContent[]>([]);
  const { getVitaContentByCategory, deleteVitaContent, isLoading } = useVitaStorage();
  const { toast } = useToast();

  useEffect(() => {
    fetchContent();
  }, [category]);

  const fetchContent = async () => {
    const contentData = await getVitaContentByCategory(category);
    setContents(contentData);
  };

  const handleDelete = async (id: string) => {
    const success = await deleteVitaContent(id);
    if (success) {
      setContents(contents.filter(content => content.id !== id));
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const getFormattedTitle = (content: VitaGeneratedContent) => {
    // Get a short title from the content
    const firstLine = content.content.split('\n')[0].trim();
    let title = firstLine;
    
    // If the first line is too long, truncate it
    if (title.length > 40) {
      title = title.substring(0, 37) + '...';
    }
    
    return title;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FilePlus className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          Plans and recommendations generated by Vita
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="loader">Loading...</div>
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No content yet. Ask Vita to create something for you!</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.href = "/vita"}
            >
              Chat with Vita
            </Button>
          </div>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {contents.map((content) => (
              <AccordionItem key={content.id} value={content.id}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex w-full justify-between items-center pr-4">
                    <span className="text-left">{getFormattedTitle(content)}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {formatDate(content.created_at)}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="whitespace-pre-wrap pb-2">
                    {content.content}
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(content.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
